import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { UserData, EventDetails } from "../types/event.types";
import { useCheckInTimer } from "./useEventTimer";
import { checkInEvent } from "../../../../../services/eventsAPI";
import { trackCheckIn } from "../../../../../services/activityService";

interface CheckInState {
  loading: boolean;
  hasCheckedIn: boolean;
  error: string | null;
}

/**
 * Hook for managing check-in functionality
 */
export const useCheckIn = (
  userData: UserData | null,
  eventDetails: EventDetails | null,
  isOrganiser: boolean,
  onCheckInSuccess?: (updatedDetails: Partial<EventDetails>) => void
) => {
  const [checkInState, setCheckInState] = useState<CheckInState>({
    loading: false,
    hasCheckedIn: false,
    error: null,
  });

  const { canCheckIn } = useCheckInTimer(eventDetails?.eventStartTime);

  useEffect(() => {
    if (eventDetails?.checkInStatus && userData?.username) {
      const checkedIn = isOrganiser
        ? eventDetails.checkInStatus.organiser
        : eventDetails.checkInStatus.challenger;

      console.log(
        `🔧 useCheckIn hook - updating hasCheckedIn for ${
          isOrganiser ? "organiser" : "challenger"
        }:`,
        {
          checkedIn: !!checkedIn,
          eventDetails: eventDetails.checkInStatus,
          username: userData.username,
          isOrganiser,
        }
      );

      setCheckInState((prev) => ({ ...prev, hasCheckedIn: !!checkedIn }));
    }
  }, [eventDetails?.checkInStatus, userData?.username, isOrganiser]);

  const handleCheckIn = useCallback(async () => {
    if (!userData || !eventDetails) {
      toast.error("Missing required information for check-in");
      return false;
    }

    if (!canCheckIn) {
      toast.error("Check-in is not available at this time");
      return false;
    }

    if (checkInState.hasCheckedIn) {
      toast.info("You have already checked in");
      return false;
    }

    setCheckInState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await checkInEvent(
        userData.AccessToken,
        eventDetails.eventID
      );

      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success("Checked in successfully!");

        trackCheckIn(userData.AccessToken, eventDetails.eventID)
          .then((result) => {
            if (result.success) {
              console.log("✅ Check-in activity tracked:", result.activityId);
            } else {
              console.warn(
                "⚠️ Failed to track check-in activity:",
                result.error
              );
            }
          })
          .catch((err) => {
            console.warn("⚠️ Error tracking check-in activity:", err);
          });

        setCheckInState((prev) => ({
          ...prev,
          loading: false,
          hasCheckedIn: true,
          error: null,
        }));

        if (onCheckInSuccess) {
          onCheckInSuccess({
            checkInStatus: {
              ...eventDetails.checkInStatus,
              [isOrganiser ? "organiser" : "challenger"]: true,
            },
          });
        }

        return true;
      } else {
        const errorMessage = response.body?.error || "Check-in failed";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error during check-in:", error);
      const errorMessage =
        error.message || "An unexpected error occurred during check-in";

      setCheckInState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      toast.error(errorMessage);
      return false;
    }
  }, [
    userData,
    eventDetails,
    canCheckIn,
    checkInState.hasCheckedIn,
    isOrganiser,
    onCheckInSuccess,
  ]);

  const canUserCheckIn = useCallback(() => {
    const result = {
      hasUserData: !!userData,
      hasEventDetails: !!eventDetails,
      hasAlreadyCheckedIn: checkInState.hasCheckedIn,
      canCheckInByTime: canCheckIn,
      eventTime: eventDetails
        ? new Date(eventDetails.eventStartTime).getTime()
        : null,
      currentTime: Date.now(),
      isEventTimeValid: eventDetails
        ? Date.now() <= new Date(eventDetails.eventStartTime).getTime()
        : false,
      finalResult: false,
    };

    if (!userData || !eventDetails || checkInState.hasCheckedIn) {
      result.finalResult = false;
    } else {
      const currentTime = Date.now();
      const eventTime = new Date(eventDetails.eventStartTime).getTime();
      result.finalResult = canCheckIn && currentTime <= eventTime;
    }

    return result.finalResult;
  }, [
    userData,
    eventDetails,
    checkInState.hasCheckedIn,
    canCheckIn,
    isOrganiser,
  ]);

  return {
    ...checkInState,
    handleCheckIn,
    canUserCheckIn: canUserCheckIn(),
  };
};
